import { toAssetProxyUrl } from "@src/components/ReverseProxy/toAssetProxyUrl";

import { ALL_GROUPS, NavigationGroup, NavigationItem } from "../types";

// Shopify Predictive Search hard-caps `limit` at 10, and with `limit_scope=each`
// that's 10 *per type* (the default scope, `all`, would cap 10 across all types
// combined). 10/type is the ceiling — the reason the unauthenticated navigator
// can't offer "load more": there are no cursors, you refine the query instead.
const MAX_RESULTS = 10;

// resources[type] takes singular resource names; suggest.json keys its results
// by the plural bucket, which matches our NavigationGroup 1:1.
const GROUP_TO_RESOURCE_TYPE: Record<NavigationGroup, string> = {
  products: "product",
  collections: "collection",
  pages: "page",
  articles: "article",
};

interface PredictiveSearchResponse {
  resources?: {
    results?: Record<string, Array<{ title?: string; url?: string }>>;
  };
}

/**
 * `available: false` means the store never answered the question — not a
 * Shopify storefront, rate-limited, blocked, erroring, or behind a password
 * page. Distinct from `available: true` with an empty `items`, which is the
 * store answering "nothing matches". Only the latter is a fact about the
 * merchant's catalog, and conflating them tells them something untrue about it.
 */
export type PredictiveOptionsResult =
  | { available: true; items: NavigationItem[] }
  | { available: false };

/**
 * Token-free storefront navigation search via Shopify's public Predictive
 * Search Ajax API, for previewing a store the logged-in org's admin token
 * can't reach (a configured demo URL). Mirrors the AI's
 * `search-destinations` tool. Relevance-ranked, up to 10 results per type.
 *
 * Anything other than a JSON 2xx yields `{ available: false }` — see the type.
 */
export async function fetchPredictiveOptions(
  origin: string,
  query: string,
  group: NavigationGroup | undefined,
  signal: AbortSignal
): Promise<PredictiveOptionsResult> {
  const types = (group ? [group] : ALL_GROUPS)
    .map((g) => GROUP_TO_RESOURCE_TYPE[g])
    .join(",");

  // Brackets are left literal — Shopify's documented query syntax.
  const suggestUrl =
    `${origin}/search/suggest.json` +
    `?q=${encodeURIComponent(query)}` +
    `&resources[type]=${types}` +
    `&resources[limit]=${MAX_RESULTS}` +
    `&resources[limit_scope]=each`;

  // The storefront's suggest.json is CORS-protected, so it can't be fetched
  // cross-origin from the app. Route it through the reverse proxy's __ASSET__
  // passthrough, which strips the prefix and proxies to the merchant.
  const url = toAssetProxyUrl(suggestUrl);

  const response = await fetch(url, {
    headers: { accept: "application/json" },
    signal,
  });

  // Anything that isn't a JSON 2xx means we didn't get to ask the question —
  // a 404 (not a Shopify storefront), a rate limit, a block, a 5xx, or a
  // password page's HTML. Reporting those as `items: []` told the user their
  // store has no matching pages, which is a different and misleading claim.
  const contentType = response.headers.get("content-type") ?? "";
  if (!response.ok || !contentType.includes("application/json")) {
    return { available: false };
  }

  const data = (await response.json()) as PredictiveSearchResponse;
  return { available: true, items: mapResults(data, group) };
}

function mapResults(
  data: PredictiveSearchResponse,
  filterGroup: NavigationGroup | undefined
): NavigationItem[] {
  const results = data.resources?.results ?? {};
  const items: NavigationItem[] = [];

  for (const group of filterGroup ? [filterGroup] : ALL_GROUPS) {
    for (const item of results[group] ?? []) {
      if (!item.title || !item.url) continue;
      items.push({ title: item.title, path: stripTracking(item.url), group });
    }
  }
  return items;
}

// Predictive Search appends analytics params (?_pos, _psq, _ss, _v) to each
// url; the navigator wants the bare storefront path so it matches the selected
// path and navigates cleanly.
function stripTracking(url: string): string {
  const queryStart = url.indexOf("?");
  return queryStart === -1 ? url : url.slice(0, queryStart);
}
